import { API_ROOT, DEFAULT_BRANCH, REPO_NAME, REPO_OWNER } from './config'
import { GitHubError } from './client'

function headers(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    Accept: 'application/vnd.github+json',
    'X-GitHub-Api-Version': '2022-11-28',
    'Content-Type': 'application/json',
  }
}

async function api<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_ROOT}${path}`, { ...init, headers: headers(token) })
  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = (await response.json()) as { message?: string }
      if (body.message) detail = body.message
    } catch {
      /* keep statusText */
    }
    throw new GitHubError(detail, response.status, response.status === 409 || response.status === 422)
  }
  return (await response.json()) as T
}

export interface FileChange {
  path: string
  content: string
}

/**
 * Land several quest edits as one reviewable commit on a branch, then open a PR.
 *
 * The Contents API commits one file at a time, which would scatter a balance pass
 * across N commits. The Git Data API builds blobs → tree → commit → ref instead, so
 * the whole pass reads as a single diff.
 */
export async function proposeChanges(
  token: string,
  branchName: string,
  changes: FileChange[],
  title: string,
  body: string,
): Promise<{ url: string; number: number }> {
  const base = await api<{ object: { sha: string } }>(
    token,
    `/repos/${REPO_OWNER}/${REPO_NAME}/git/ref/heads/${DEFAULT_BRANCH}`,
  )
  const baseSha = base.object.sha

  const baseCommit = await api<{ tree: { sha: string } }>(
    token,
    `/repos/${REPO_OWNER}/${REPO_NAME}/git/commits/${baseSha}`,
  )

  const blobs = await Promise.all(
    changes.map(async (change) => {
      const blob = await api<{ sha: string }>(token, `/repos/${REPO_OWNER}/${REPO_NAME}/git/blobs`, {
        method: 'POST',
        body: JSON.stringify({ content: change.content, encoding: 'utf-8' }),
      })
      return { path: change.path, sha: blob.sha }
    }),
  )

  const tree = await api<{ sha: string }>(token, `/repos/${REPO_OWNER}/${REPO_NAME}/git/trees`, {
    method: 'POST',
    body: JSON.stringify({
      base_tree: baseCommit.tree.sha,
      tree: blobs.map((blob) => ({
        path: blob.path,
        mode: '100644',
        type: 'blob',
        sha: blob.sha,
      })),
    }),
  })

  const commit = await api<{ sha: string }>(token, `/repos/${REPO_OWNER}/${REPO_NAME}/git/commits`, {
    method: 'POST',
    body: JSON.stringify({ message: title, tree: tree.sha, parents: [baseSha] }),
  })

  await api(token, `/repos/${REPO_OWNER}/${REPO_NAME}/git/refs`, {
    method: 'POST',
    body: JSON.stringify({ ref: `refs/heads/${branchName}`, sha: commit.sha }),
  })

  const pull = await api<{ html_url: string; number: number }>(
    token,
    `/repos/${REPO_OWNER}/${REPO_NAME}/pulls`,
    {
      method: 'POST',
      body: JSON.stringify({ title, body, head: branchName, base: DEFAULT_BRANCH }),
    },
  )
  return { url: pull.html_url, number: pull.number }
}

export function branchNameFor(summary: string): string {
  const slug = summary
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
  const stamp = new Date().toISOString().slice(0, 10)
  return `edit/${stamp}-${slug || 'quest-changes'}`
}
